import { toast } from "../../../global-account/react/index.js";
import { createAssociatedTokenAccountInstruction, createTransferCheckedInstruction, getAssociatedTokenAddressSync } from "@solana/spl-token";
import { ComputeBudgetProgram, Connection, PublicKey, SystemProgram, Transaction } from "@solana/web3.js";
import { useCallback, useMemo } from "react";
/**
 * Custom hook for handling Phantom wallet transfers on Solana.
 * Supports both native SOL and SPL token transfers with automatic priority fee calculation.
 *
 * @example
 * ```tsx
 * const { initiateTransfer, isPhantomAvailable } = usePhantomTransfer();
 *
 * await initiateTransfer({
 *   amountLamports: "1000000000", // 1 SOL
 *   tokenAddress: "11111111111111111111111111111111",
 *   recipientAddress: "..."
 * });
 * ```
 */
export function usePhantomTransfer({
  rpcEndpoint
} = {}) {
  // Default RPC endpoint
  const defaultRpcEndpoint = "https://mainnet.helius-rpc.com/?api-key=efafd9b3-1807-4cf8-8aa4-3d984f56d8fb";
  const effectiveRpcEndpoint = rpcEndpoint || defaultRpcEndpoint;
  // Check for Phantom wallet availability
  const isPhantomMobile = useMemo(() => navigator.userAgent.includes("Phantom"), []);
  const isPhantomBrowser = useMemo(() => window.phantom?.solana?.isPhantom, []);
  const isPhantomAvailable = isPhantomMobile || isPhantomBrowser;
  /**
   * Get the connected Phantom wallet address if available
   */
  const getConnectedAddress = useCallback(() => {
    const phantom = window.phantom?.solana;
    if (phantom?.isConnected && phantom?.publicKey) {
      return phantom.publicKey.toString();
    }
    return null;
  }, []);
  /**
   * Calculate optimal priority fee based on recent network activity
   */
  const calculatePriorityFee = useCallback(async (connection, fromPubkey) => {
    let priorityFee = 10000; // Default fallback (10,000 micro-lamports)
    try {
      const recentFees = await connection.getRecentPrioritizationFees({
        lockedWritableAccounts: [fromPubkey]
      });
      if (recentFees && recentFees.length > 0) {
        // Use 75th percentile of recent fees for good priority
        const sortedFees = recentFees.map(fee => fee.prioritizationFee).sort((a, b) => a - b);
        const percentile75Index = Math.floor(sortedFees.length * 0.75);
        priorityFee = Math.max(sortedFees[percentile75Index] || 10000, 10000);
      }
    } catch (feeError) {
      console.warn("Failed to fetch recent priority fees, using default:", feeError);
    }
    return priorityFee;
  }, []);
  /**
   * Create a native SOL transfer transaction with priority fees
   */
  const createNativeTransferTransaction = useCallback(async (_connection, fromPubkey_0, toPubkey, amount, priorityFee_0) => {
    const computeUnitLimit = 1000; // SOL transfer + compute budget instructions need ~600-800 CU
    const computeUnitPrice = Math.min(priorityFee_0, 100000); // Cap at 100k micro-lamports for safety
    const transaction = new Transaction().add(
    // Set compute unit limit first (must come before other instructions)
    ComputeBudgetProgram.setComputeUnitLimit({
      units: computeUnitLimit
    })).add(
    // Set priority fee
    ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: computeUnitPrice
    })).add(
    // Actual transfer instruction
    SystemProgram.transfer({
      fromPubkey: fromPubkey_0,
      toPubkey,
      lamports: Number(amount)
    }));
    console.log(`Using priority fee: ${computeUnitPrice} micro-lamports per CU, limit: ${computeUnitLimit} CU`);
    return transaction;
  }, []);
  /**
   * Create an SPL token transfer transaction with priority fees
   */
  const createSPLTransferTransaction = useCallback(async (connection_0, fromPubkey_1, toPubkey_0, mintPubkey, amount_0, priorityFee_1) => {
    // Get associated token accounts
    const fromTokenAccount = getAssociatedTokenAddressSync(mintPubkey, fromPubkey_1);
    const toTokenAccount = getAssociatedTokenAddressSync(mintPubkey, toPubkey_0);
    // Check if destination token account exists
    const toTokenAccountInfo = await connection_0.getAccountInfo(toTokenAccount);
    const needsDestinationAccount = !toTokenAccountInfo;
    // Get mint info to determine decimals
    const mintInfo = await connection_0.getParsedAccountInfo(mintPubkey);
    const decimals = mintInfo.value?.data?.parsed?.info?.decimals || 9;
    // SPL transfers need more compute units than SOL transfers
    // Add extra CU if we need to create destination account
    const computeUnitLimit_0 = needsDestinationAccount ? 40000 : 20000;
    const computeUnitPrice_0 = Math.min(priorityFee_1, 100000);
    // Create transfer instruction
    const transferInstruction = createTransferCheckedInstruction(fromTokenAccount, mintPubkey, toTokenAccount, fromPubkey_1, Number(amount_0), decimals);
    const transaction_0 = new Transaction().add(ComputeBudgetProgram.setComputeUnitLimit({
      units: computeUnitLimit_0
    })).add(ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: computeUnitPrice_0
    }));
    // Add create destination account instruction if needed
    if (needsDestinationAccount) {
      transaction_0.add(createAssociatedTokenAccountInstruction(fromPubkey_1,
      // payer
      toTokenAccount,
      // ata
      toPubkey_0,
      // owner
      mintPubkey));
    }
    // Add the transfer instruction
    transaction_0.add(transferInstruction);
    console.log(`SPL Token transfer: ${computeUnitPrice_0} micro-lamports per CU, limit: ${computeUnitLimit_0} CU, creating destination: ${needsDestinationAccount}`);
    return transaction_0;
  }, []);
  /**
   * Initiate a Phantom wallet transfer for SOL or SPL tokens
   */
  const initiateTransfer = useCallback(async ({
    amountLamports,
    tokenAddress,
    recipientAddress
  }) => {
    try {
      // Step 1: Check if Phantom is installed
      if (!isPhantomAvailable) {
        toast.error("Phantom wallet not installed. Please install Phantom wallet to continue.");
        return;
      }
      // Step 2: Ensure Phantom is connected/unlocked
      const phantom_0 = window.phantom?.solana;
      if (!phantom_0) {
        toast.error("Phantom wallet not accessible");
        return;
      }
      // Connect and unlock wallet if needed
      let publicKey;
      try {
        const connection_1 = await phantom_0.connect();
        publicKey = connection_1.publicKey;
      } catch (connectError) {
        toast.error("Failed to connect to Phantom wallet");
        return;
      }
      // Step 3: Setup connection and public keys
      const connection_2 = new Connection(effectiveRpcEndpoint);
      const fromPubkey_2 = new PublicKey(publicKey.toString());
      const toPubkey_1 = new PublicKey(recipientAddress);
      const amount_1 = BigInt(amountLamports);
      // Step 4: Calculate optimal priority fee
      const priorityFee_2 = await calculatePriorityFee(connection_2, fromPubkey_2);
      // Step 5: Create transaction based on token type
      let transaction_1;
      if (tokenAddress === "11111111111111111111111111111111") {
        // Native SOL transfer
        transaction_1 = await createNativeTransferTransaction(connection_2, fromPubkey_2, toPubkey_1, amount_1, priorityFee_2);
      } else {
        // SPL Token transfer
        const mintPubkey_0 = new PublicKey(tokenAddress);
        transaction_1 = await createSPLTransferTransaction(connection_2, fromPubkey_2, toPubkey_1, mintPubkey_0, amount_1, priorityFee_2);
      }
      // Step 6: Get latest blockhash and set fee payer
      const {
        blockhash
      } = await connection_2.getLatestBlockhash("confirmed");
      transaction_1.recentBlockhash = blockhash;
      transaction_1.feePayer = fromPubkey_2;
      // Step 7: Sign and send transaction
      const signedTransaction = await phantom_0.signAndSendTransaction(transaction_1);
      toast.success(`Transaction successful! Signature: ${signedTransaction.signature}`);
      console.log("Transaction sent with priority fees. Signature:", signedTransaction.signature);
    } catch (error) {
      console.error("Transfer error:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("User rejected")) {
        toast.error("Transaction was cancelled by user");
      } else if (errorMessage.includes("insufficient")) {
        toast.error("Insufficient balance for this transaction");
      } else if (errorMessage.includes("blockhash not found")) {
        toast.error("Network congestion detected. Please try again in a moment.");
      } else {
        toast.error(`Transfer failed: ${errorMessage}`);
      }
    }
  }, [isPhantomAvailable, effectiveRpcEndpoint, calculatePriorityFee, createNativeTransferTransaction, createSPLTransferTransaction]);
  return {
    /** Function to initiate a transfer */
    initiateTransfer,
    /** Whether Phantom wallet is available (installed) */
    isPhantomAvailable,
    /** Get the currently connected Phantom wallet address */
    getConnectedAddress
  };
}